import { useCallback, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { useMeContext } from 'modules/me';
import { Period } from 'graphql/types';
import { formatDateFromIsoString } from 'utils/date-time';
import { getNextPeriod, getPreviousPeriod } from 'utils/date-range';
import { LoadMorePeriodType } from 'modules/smartDiary/smartDiaryNotes/hooks';
import {
  SmartDiaryViewOptions,
  useSmartTimesheetContext
} from '../../smartDiaryNotes/SmartTimesheetContextProvider';

type TimesheetPeriodNavigatorReturnType = {
  handlePreviousClick: () => void;
  handleNextClick: () => void;
  timesheetPeriodString: string;
  disablePrevious: boolean;
  disableNext: boolean;
};

export const useTimesheetPeriodNavigatorActions = (
  loadMore: LoadMorePeriodType
): TimesheetPeriodNavigatorReturnType => {
  const me = useMeContext();

  const {
    timesheetPeriods,
    currentPeriod,
    setCurrentPeriod,
    displayTimesheetPeriods,
    selectedViewOption,
    setSelectedViewOption
  } = useSmartTimesheetContext();

  const indexOfCurrentPeriod = timesheetPeriods.findIndex(
    (item) => item === currentPeriod
  );

  const [currentIndex, setCurrentIndex] = useState(indexOfCurrentPeriod);
  const [disablePrevious, setDisablePrevious] = useState(false);
  const [disableNext, setDisableNext] = useState(
    displayTimesheetPeriods
      ? currentIndex >= timesheetPeriods.length - 1
      : currentPeriod?.startDate === DateTime.local().toISODate()
  );
  const [displayPeriods, setDisplayPeriods] = useState(displayTimesheetPeriods);

  const enableOrDisableNavigation = useCallback(
    (index: number, periods: Period[]) => {
      if (!periods.length) {
        setDisablePrevious(true);
      } else {
        setDisablePrevious(index < 0);
        setDisableNext(index >= periods.length - 1);
      }
    },
    [setDisablePrevious, setDisableNext]
  );

  const updateIndexAndCurrentPeriod = useCallback(
    (periods: Period[]) => {
      if (periods.length) {
        const indexOfCurrentPeriod = periods.findIndex(
          (item) => item === currentPeriod
        );
        if (indexOfCurrentPeriod > 0) {
          const newIndex = indexOfCurrentPeriod - 1;
          setCurrentIndex(newIndex);
          setCurrentPeriod(periods[newIndex]);
          enableOrDisableNavigation(newIndex, periods);
        } else if (indexOfCurrentPeriod === 0) {
          setCurrentIndex(indexOfCurrentPeriod);
          setCurrentPeriod(periods[indexOfCurrentPeriod]);
          enableOrDisableNavigation(indexOfCurrentPeriod, periods);
        }
      } else {
        enableOrDisableNavigation(currentIndex, []);
      }
    },
    [currentIndex, currentPeriod, enableOrDisableNavigation, setCurrentPeriod]
  );

  const handlePreviousClick = useCallback(async () => {
    if (selectedViewOption !== SmartDiaryViewOptions.Custom) {
      setSelectedViewOption(SmartDiaryViewOptions.Custom);
    }
    if (displayPeriods) {
      if (currentIndex === 0) {
        const periods = await loadMore({
          startDate: DateTime.fromISO(currentPeriod?.startDate)
            .minus({
              months: 2,
              days: 1
            })
            .startOf('day'),
          endDate: DateTime.fromISO(currentPeriod?.startDate)
            .minus({
              days: 1
            })
            .endOf('day')
        });
        updateIndexAndCurrentPeriod(periods);
      } else {
        const index = currentIndex - 1;
        setCurrentIndex(index);
        if (index >= 0) {
          setCurrentPeriod(timesheetPeriods[index]);
        }
      }
    } else {
      const { start, end } = getPreviousPeriod(
        DateTime.fromISO(currentPeriod?.startDate),
        DateTime.fromISO(currentPeriod?.endDate)
      );
      setCurrentPeriod({
        startDate: start?.toISODate(),
        endDate: end?.toISODate()
      });
    }
  }, [
    currentIndex,
    currentPeriod,
    loadMore,
    setCurrentPeriod,
    selectedViewOption,
    setSelectedViewOption,
    timesheetPeriods,
    updateIndexAndCurrentPeriod,
    displayPeriods
  ]);

  const handleNextClick = useCallback(() => {
    if (selectedViewOption !== SmartDiaryViewOptions.Custom) {
      setSelectedViewOption(SmartDiaryViewOptions.Custom);
    }

    if (displayPeriods) {
      const index = currentIndex + 1;
      setCurrentIndex(index);
      if (index < timesheetPeriods.length) {
        setCurrentPeriod(timesheetPeriods[index]);
      }
    } else {
      const { start, end } = getNextPeriod(
        DateTime.fromISO(currentPeriod?.startDate),
        DateTime.fromISO(currentPeriod?.endDate)
      );
      setCurrentPeriod({
        startDate: start?.toISODate(),
        endDate: end?.toISODate()
      });
    }
  }, [
    currentIndex,
    currentPeriod,
    setCurrentPeriod,
    timesheetPeriods,
    selectedViewOption,
    setSelectedViewOption,
    displayPeriods
  ]);

  useMemo(() => {
    if (displayPeriods !== displayTimesheetPeriods) {
      setDisplayPeriods(displayTimesheetPeriods);
    }
    if (displayTimesheetPeriods) {
      if (timesheetPeriods.length) {
        const indexOfCurrentPeriod = timesheetPeriods.findIndex(
          (item) => item === currentPeriod
        );
        setCurrentIndex(indexOfCurrentPeriod);
        enableOrDisableNavigation(indexOfCurrentPeriod, timesheetPeriods);
      }
    } else {
      const isToday = currentPeriod?.startDate === DateTime.local().toISODate();
      setDisablePrevious(false);
      setDisableNext(isToday);
    }
  }, [
    currentPeriod,
    displayPeriods,
    displayTimesheetPeriods,
    enableOrDisableNavigation,
    timesheetPeriods
  ]);

  const timesheetPeriodString = useMemo(() => {
    const { startDate, endDate } = currentPeriod ?? {};

    const formattedStartDate = startDate
      ? formatDateFromIsoString(startDate, me)
      : '-';
    const formattedEndDate = endDate
      ? formatDateFromIsoString(endDate, me)
      : '-';

    return formattedStartDate + ' - ' + formattedEndDate;
  }, [currentPeriod, me]);

  return useMemo(
    () => ({
      handlePreviousClick,
      handleNextClick,
      timesheetPeriodString,
      disablePrevious,
      disableNext
    }),
    [
      disableNext,
      disablePrevious,
      handleNextClick,
      handlePreviousClick,
      timesheetPeriodString
    ]
  );
};
